import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { LoginScreenImage } from "../../../assets";
import { setToStorage } from "../../../utils";
import { useNavigate } from "react-router";
import { ROUTES_CONSTANTS } from "../../../constants";
import { PiEyeLight, PiEyeSlash } from "react-icons/pi";
import { Button, AuthSplitLayout } from "../../../components";
import { loginApi } from "../../../DAL";

const Login = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [viewPassword, setViewPassword] = useState(false);

  const schema = useMemo(
    () =>
      yup.object({
        email: yup
          .string()
          .email(t("validation.emailInvalid"))
          .required(t("validation.emailRequired")),
        password: yup
          .string()
          .min(6, t("validation.passwordMin6"))
          .required(t("validation.passwordRequired")),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const mutation = useMutation({
    mutationFn: loginApi,
    onSuccess: (data) => {
      setToStorage("accessToken", data.data.token);
      navigate(ROUTES_CONSTANTS.NO_ROUTE);
    },
  });

  const onSubmit = (data) => {
    mutation.mutate(data);
  };

  const imageOverlay = (
    <div className="absolute inset-x-0 bottom-0 h-70 bg-primary-500 text-white lg:text-[2rem] xl:text-[2.5rem] xl:py-15 xl:px-20 lg:py-9 lg:px-12 text-center">
      {t("login.imageTagline")}
    </div>
  );

  return (
    <AuthSplitLayout
      imageSrc={LoginScreenImage}
      imageAlt="login-screen-image"
      imageOverlay={imageOverlay}
      panelClassName="border-4 border-primary-500"
    >
      <div className="w-full max-w-sm lg:w-[50%]">
        <div className="text-3xl sm:text-4xl font-medium text-black mb-2">
          {t("login.title")}
        </div>
        <div className="text-lg sm:text-xl mb-8">
          {t("login.subtitle")}
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Email */}
          <div className="mb-4">
            <label className="font-medium">{t("login.emailLabel")}</label>
            <input
              type="email"
              placeholder={t("login.emailPlaceholder")}
              {...register("email")}
              className="w-full border rounded-lg p-3 mt-2"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Password */}
          <div className="mb-4">
            <label className="font-medium">{t("login.passwordLabel")}</label>
            <div className="relative">
              <input
                type={viewPassword ? "text" : "password"}
                placeholder={t("login.passwordPlaceholder")}
                {...register("password")}
                className="w-full border rounded-lg p-3 mt-2"
              />
              {viewPassword ? (
                <PiEyeSlash
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer"
                  onClick={() => setViewPassword(false)}
                />
              ) : (
                <PiEyeLight
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer"
                  onClick={() => setViewPassword(true)}
                />
              )}
            </div>
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">
                {errors.password.message}
              </p>
            )}
          </div>

          <div
            className="flex justify-end cursor-pointer text-primary-500 mb-4"
            onClick={() => navigate(ROUTES_CONSTANTS.FORGOT_PASSWORD)}
          >
            {t("login.forgotPassword")}
          </div>

          <Button
            type="submit"
            loading={mutation.isPending}
            fullWidth
            size="lg"
          >
            {mutation.isPending ? t("login.btnLoading") : t("login.btnSubmit")}
          </Button>
        </form>
      </div>
    </AuthSplitLayout>
  );
};

export default Login;
